import { Chain } from "@rainbow-me/rainbowkit";
import { differenceBy } from "lodash-es";
import percentRound from "percent-round";
import { To } from "react-router-dom";
import { BEBOP_ROUTES, MAX_SELECTABLE_TOKENS } from "../config/consts";
import {
  GlobalStore,
  INITIAL_STATE,
  SelectedToken,
  WithAllocationPct,
} from "../store/global/GlobalStore";
import { Token } from "../store/global/TokenEnum";
import { TokenMetadata } from "../store/global/getTokenMetadata";
import { Amount } from "../types/amount";
import { SUPPORTED_NETWORKS } from "../types/network";

export const URL_KEYS = {
  NETWORK: "network",
  FROM: "sell",
  TO: "buy",
};

export const isValidToken = (
  maybeToken: string,
  tokenMetadata: TokenMetadata,
  toEntries: Array<string>,
  fromEntries: Array<string>
): boolean => {
  const tokenIsApplicableToNetwork = tokenMetadata[maybeToken as Token] !== undefined;
  const toAndFromIncludeSameToken = toEntries.some(entry => fromEntries.includes(entry));
  if (
    Object.values(Token).includes(maybeToken as Token) &&
    tokenIsApplicableToNetwork &&
    !toAndFromIncludeSameToken
  ) {
    return true;
  }
  return false;
};
export const processMakerTokensFromURL = (
  queryParamTo: string,
  tokenMetadata: TokenMetadata,
  queryParamFrom: string
): Array<SelectedToken & WithAllocationPct> => {
  const toEntries = queryParamTo.split(",");
  const fromEntries = queryParamFrom.split(",");
  const toAndFromIncludeSameToken = toEntries.some(entry => fromEntries.includes(entry));
  if (toAndFromIncludeSameToken) {
    updateUrlParams(URL_KEYS.TO, "", window.location.pathname);
  }

  const slicedEntries = toEntries.slice(0, MAX_SELECTABLE_TOKENS);
  const makerTokens = slicedEntries
    .filter(entry => isValidToken(entry, tokenMetadata, toEntries, fromEntries))
    .map((entry, index, arr) => {
      return {
        token: entry as Token,
        amount: Amount.ZERO,
        pct: 100 / arr.length,
        isLocked: false,
      };
    });
  const roundedPercentages = percentRound(makerTokens.map(entry => entry.pct));
  return makerTokens.map((entry, index) => {
    entry.pct = roundedPercentages[index];
    return entry;
  });
};

export const processTakerTokensFromURL = (
  queryParamFrom: string,
  tokenMetadata: TokenMetadata,
  queryParamTo: string
): Array<SelectedToken> => {
  const toEntries = queryParamTo.split(",");
  const fromEntries = queryParamFrom.split(",");

  return queryParamFrom
    .split(",")
    .slice(0, MAX_SELECTABLE_TOKENS)
    .filter(entry => isValidToken(entry, tokenMetadata, toEntries, fromEntries))
    .map(entry => {
      return {
        token: entry as Token,
        amount: Amount.ZERO,
      };
    });
};

export const processNetworkFromUrl = (networkQueryParam: string | null): Chain | undefined => {
  const potentialMatch = SUPPORTED_NETWORKS.find(chain => {
    return chain.name.toLowerCase() === networkQueryParam?.toLowerCase();
  });
  return potentialMatch;
};
export const processTokensFromUrl = (
  from: string | null,
  to: string | null,
  tokenMetadata: TokenMetadata
): void => {
  GlobalStore.update(s => {
    const takerTokens = processTakerTokensFromURL(from ?? "", tokenMetadata, to ?? "");
    const makerTokens = processMakerTokensFromURL(to ?? "", tokenMetadata, from ?? "");
    // If we navigate back to OrderEntry from Order Review - we shouldn't recreate the tokens
    const changedMakerTokens = differenceBy(makerTokens, s.makerTokens, "token");
    const changedTakerTokens = differenceBy(takerTokens, s.takerTokens, "token");
    // Protect against many-to-many edge case.
    const isManyToMany = takerTokens.length > 1 && makerTokens.length > 1;
    if (changedTakerTokens.length > 0) {
      s.takerTokens = takerTokens;
    }
    if (changedMakerTokens.length > 0) {
      s.makerTokens = !isManyToMany ? makerTokens : [makerTokens[0]];
    }
  });
};

/**
 * Updates the query params on the URL without reloading the page.
 * @param key: String
 * @param value: String
 */
export const updateUrlParams = (key: string, value: string, pathname: string): void => {
  if ((pathname as BEBOP_ROUTES) !== BEBOP_ROUTES.Trade) {
    return;
  }
  const searchParams = new URLSearchParams(window.location.search);
  searchParams.set(key, value);
  const newUrl =
    window.location.protocol +
    "//" +
    window.location.host +
    window.location.pathname +
    "?" +
    searchParams.toString();
  window.history.pushState({ path: newUrl }, "", newUrl);
};

export const removeUrlParam = (key: string): void => {
  if (typeof URLSearchParams !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    params.delete(key);
  }
};

export const initURLParams = () => {
  // updateUrlParams(URL_KEYS.FROM, INITIAL_STATE.takerTokens.map(e => e.token.toString()).join(","));
  updateUrlParams(
    URL_KEYS.NETWORK,
    INITIAL_STATE.network?.name.toLowerCase() ?? "",
    window.location.pathname
  );
};

export const isInternalPath = (to: To) => {
  if (typeof to === "string" && to.startsWith("/")) {
    return true;
  }

  const currentUrl = new URL(window.location.href);
  let toString = undefined;

  if (typeof to === "string") {
    toString = to;
  } else {
    if (to.pathname != null) {
      toString = to.pathname + (to.search ?? "") + (to.hash ?? "");
    }
  }
  if (toString == null) {
    console.warn("Not a valid pathname");
    return false;
  }
  const targetUrl = toString.startsWith("//")
    ? new URL(currentUrl.protocol + toString)
    : new URL(toString);
  return targetUrl.origin === currentUrl.origin;
};
